import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { timesheetApi } from '../services/api'
import toast from 'react-hot-toast'

const TOOLS = [
    { id: 'pen', label: 'Pen' },
    { id: 'eraser', label: 'Eraser' },
    { id: 'line', label: 'Straight' },
    { id: 'arrow', label: 'Arrow' },
    { id: 'circle', label: 'Circle' },
    { id: 'arc', label: 'Arc' },
    { id: 'box', label: 'Box' },
    { id: 'text', label: 'Text' },
]

export default function SketchPad() {
    const { uuid } = useParams()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const drawingId = searchParams.get('drawingId')
    const referenceNo = searchParams.get('referenceNo')

    const canvasRef = useRef(null)
    const ctxRef = useRef(null)
    const containerRef = useRef(null)

    const [tool, setTool] = useState('pen')
    const [saving, setSaving] = useState(false)
    const [currentDrawingId, setCurrentDrawingId] = useState(drawingId || '')
    const [currentTitle, setCurrentTitle] = useState('')

    // Drawing state refs (avoid re-renders during drawing)
    const drawingRef = useRef(false)
    const startXRef = useRef(0)
    const startYRef = useRef(0)
    const toolRef = useRef('pen')
    const tempImageRef = useRef(null)
    const undoStackRef = useRef([])
    const textInputRef = useRef(null)

    // Keep toolRef in sync
    useEffect(() => {
        toolRef.current = tool
    }, [tool])

    // Initialize canvas
    useEffect(() => {
        const canvas = canvasRef.current
        const container = containerRef.current
        if (!canvas || !container) return

        const ctx = canvas.getContext('2d', { willReadFrequently: true })
        ctxRef.current = ctx

        function resize() {
            const oldData = canvas.width > 0 && canvas.height > 0
                ? ctx.getImageData(0, 0, canvas.width, canvas.height)
                : null
            canvas.width = container.clientWidth
            canvas.height = container.clientHeight
            if (oldData) ctx.putImageData(oldData, 0, 0)
        }

        resize()
        window.addEventListener('resize', resize)

        // Load existing drawing if editing
        if (drawingId) {
            loadDrawing(drawingId)
        }

        return () => window.removeEventListener('resize', resize)
    }, [])

    // Create floating text input
    useEffect(() => {
        const input = document.createElement('input')
        input.type = 'text'
        input.style.position = 'fixed'
        input.style.display = 'none'
        input.style.border = '2px solid #2563eb'
        input.style.font = '16px Arial'
        input.style.padding = '4px 8px'
        input.style.borderRadius = '4px'
        input.style.zIndex = '9999'
        input.style.background = 'white'
        document.body.appendChild(input)
        textInputRef.current = input

        return () => {
            if (input.parentNode) input.parentNode.removeChild(input)
        }
    }, [])

    async function loadDrawing(id) {
        try {
            const response = await timesheetApi.getDrawing(id)
            if (response.success && response.drawing) {
                const img = new Image()
                img.onload = () => {
                    const ctx = ctxRef.current
                    const canvas = canvasRef.current
                    ctx.clearRect(0, 0, canvas.width, canvas.height)
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
                }
                img.src = response.drawing.drawingData
                setCurrentDrawingId(response.drawing.id || id)
                setCurrentTitle(response.drawing.drawingTitle || '')
            }
        } catch (error) {
            console.error('Error loading drawing:', error)
        }
    }

    // --- Pointer helpers ---
    function getPointerPos(e) {
        const canvas = canvasRef.current
        if (!canvas) return { x: 0, y: 0 }
        const rect = canvas.getBoundingClientRect()
        let clientX, clientY
        if (e.touches && e.touches.length > 0) {
            clientX = e.touches[0].clientX
            clientY = e.touches[0].clientY
        } else {
            clientX = e.clientX
            clientY = e.clientY
        }
        const scaleX = canvas.width / rect.width
        const scaleY = canvas.height / rect.height
        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        }
    }

    function getClientPos(e) {
        if (e.touches && e.touches.length > 0) {
            return { x: e.touches[0].clientX, y: e.touches[0].clientY }
        }
        return { x: e.clientX, y: e.clientY }
    }

    // --- State helpers ---
    function saveState() {
        const ctx = ctxRef.current
        const canvas = canvasRef.current
        undoStackRef.current.push(ctx.getImageData(0, 0, canvas.width, canvas.height))
    }

    function handleUndo() {
        if (undoStackRef.current.length > 0) {
            const previous = undoStackRef.current.pop()
            ctxRef.current.putImageData(previous, 0, 0)
        }
    }

    function handleClear() {
        saveState()
        const canvas = canvasRef.current
        ctxRef.current.clearRect(0, 0, canvas.width, canvas.height)
    }

    // --- Text tool ---
    function addFloatingText(e) {
        e.preventDefault()
        const pos = getPointerPos(e)
        const clientPos = getClientPos(e)
        const input = textInputRef.current
        if (!input) return

        input.style.left = `${clientPos.x}px`
        input.style.top = `${clientPos.y}px`
        input.style.display = 'block'
        input.value = ''
        input.focus()

        const canvasX = pos.x
        const canvasY = pos.y

        input.onkeydown = (event) => {
            if (event.key === 'Enter') {
                commitText(canvasX, canvasY)
            } else if (event.key === 'Escape') {
                input.style.display = 'none'
            }
        }

        input.onblur = () => {
            if (input.value.trim() !== '') {
                commitText(canvasX, canvasY)
            }
            input.style.display = 'none'
        }
    }

    function commitText(x, y) {
        const ctx = ctxRef.current
        const input = textInputRef.current
        saveState()
        ctx.save()
        ctx.fillStyle = 'black'
        ctx.font = '16px Arial'
        ctx.fillText(input.value, x, y)
        ctx.restore()
        input.style.display = 'none'
        input.value = ''
    }

    // --- Drawing functions ---
    function startDraw(e) {
        e.preventDefault()
        const currentTool = toolRef.current

        if (currentTool === 'text') {
            addFloatingText(e)
            return
        }

        drawingRef.current = true
        const pos = getPointerPos(e)
        startXRef.current = pos.x
        startYRef.current = pos.y

        saveState()

        const ctx = ctxRef.current
        const canvas = canvasRef.current

        if (currentTool === 'pen' || currentTool === 'eraser') {
            ctx.beginPath()
            ctx.moveTo(pos.x, pos.y)
        } else {
            tempImageRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height)
        }
    }

    function drawMove(e) {
        if (!drawingRef.current) return
        e.preventDefault()

        const ctx = ctxRef.current
        const pos = getPointerPos(e)
        const x = pos.x
        const y = pos.y
        const currentTool = toolRef.current
        const sx = startXRef.current
        const sy = startYRef.current

        if (currentTool === 'eraser') {
            ctx.strokeStyle = 'white'
            ctx.lineWidth = 20
            ctx.lineCap = 'round'
            ctx.lineTo(x, y)
            ctx.stroke()
            return
        }

        if (currentTool === 'pen') {
            ctx.strokeStyle = 'black'
            ctx.lineWidth = 2
            ctx.lineCap = 'round'
            ctx.lineTo(x, y)
            ctx.stroke()
            return
        }

        // Shape tools - restore temp image then draw preview
        ctx.putImageData(tempImageRef.current, 0, 0)
        ctx.strokeStyle = 'black'
        ctx.lineWidth = 2

        switch (currentTool) {
            case 'line':
                ctx.beginPath()
                ctx.moveTo(sx, sy)
                ctx.lineTo(x, y)
                ctx.stroke()
                break

            case 'arrow': {
                ctx.beginPath()
                ctx.moveTo(sx, sy)
                ctx.lineTo(x, y)
                ctx.stroke()
                const headLength = 15
                const angle = Math.atan2(y - sy, x - sx)
                ctx.beginPath()
                ctx.moveTo(x, y)
                ctx.lineTo(
                    x - headLength * Math.cos(angle - Math.PI / 6),
                    y - headLength * Math.sin(angle - Math.PI / 6)
                )
                ctx.lineTo(
                    x - headLength * Math.cos(angle + Math.PI / 6),
                    y - headLength * Math.sin(angle + Math.PI / 6)
                )
                ctx.lineTo(x, y)
                ctx.fillStyle = 'black'
                ctx.fill()
                break
            }

            case 'box':
                ctx.strokeRect(
                    Math.min(sx, x), Math.min(sy, y),
                    Math.abs(x - sx), Math.abs(y - sy)
                )
                break

            case 'circle': {
                const radius = Math.hypot(x - sx, y - sy)
                ctx.beginPath()
                ctx.arc(sx, sy, radius, 0, Math.PI * 2)
                ctx.stroke()
                break
            }

            case 'arc': {
                const r = Math.hypot(x - sx, y - sy)
                const dx = x - sx
                const dy = y - sy
                ctx.beginPath()
                if (Math.abs(dx) > Math.abs(dy)) {
                    if (dx < 0) {
                        ctx.arc(sx, sy, r, Math.PI / 2, 3 * Math.PI / 2, false)
                    } else {
                        ctx.arc(sx, sy, r, -Math.PI / 2, Math.PI / 2, false)
                    }
                } else {
                    if (dy < 0) {
                        ctx.arc(sx, sy, r, Math.PI, 0, false)
                    } else {
                        ctx.arc(sx, sy, r, 0, Math.PI, false)
                    }
                }
                ctx.stroke()
                break
            }
        }
    }

    function endDraw(e) {
        if (!drawingRef.current) return
        drawingRef.current = false
        if (e) drawMove(e)
    }

    // --- Save drawing ---
    async function handleSave() {
        const canvas = canvasRef.current
        const ctx = ctxRef.current

        // Check if canvas is empty
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const pixels = imageData.data
        let isEmpty = true
        for (let i = 3; i < pixels.length; i += 4) {
            if (pixels[i] !== 0) { isEmpty = false; break }
        }
        if (isEmpty) {
            toast.error('Canvas is empty. Please draw something before saving.')
            return
        }

        const title = prompt('Enter a title for this drawing:', currentTitle || '')
        if (title === null) return
        if (!title.trim()) {
            toast.error('Please enter a drawing title.')
            return
        }

        // Create white background version
        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = canvas.width
        tempCanvas.height = canvas.height
        const tempCtx = tempCanvas.getContext('2d')
        tempCtx.fillStyle = 'white'
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height)
        tempCtx.drawImage(canvas, 0, 0)
        const drawingData = tempCanvas.toDataURL('image/png')

        setSaving(true)
        try {
            const response = await timesheetApi.saveDrawing({
                drawingId: currentDrawingId || null,
                referenceNo: referenceNo,
                timesheetUuid: uuid,
                drawingTitle: title.trim(),
                drawingData: drawingData
            })
            if (response.success) {
                setCurrentDrawingId(response.drawingId || currentDrawingId)
                setCurrentTitle(title.trim())
                toast.success('Drawing saved!')
                navigate(`/timesheet/${uuid}/sketches?referenceNo=${encodeURIComponent(referenceNo)}`)
            } else {
                toast.error(response.message || 'Failed to save drawing')
            }
        } catch (error) {
            toast.error('Error saving drawing')
        }
        setSaving(false)
    }

    function handleBack() {
        navigate(`/timesheet/${uuid}/sketches?referenceNo=${encodeURIComponent(referenceNo)}`)
    }

    // --- Touch/Mouse event handlers via useCallback ---
    const onPointerDown = useCallback((e) => startDraw(e), [])
    const onPointerMove = useCallback((e) => drawMove(e), [])
    const onPointerUp = useCallback((e) => endDraw(e), [])

    return (
        <div className="fixed inset-0 bg-white flex flex-col z-50">
            {/* Canvas area */}
            <div ref={containerRef} className="flex-1 overflow-hidden touch-none">
                <canvas
                    ref={canvasRef}
                    className={`w-full h-full ${tool === 'eraser' ? 'cursor-cell' : tool === 'text' ? 'cursor-text' : 'cursor-crosshair'}`}
                    onMouseDown={onPointerDown}
                    onMouseMove={onPointerMove}
                    onMouseUp={onPointerUp}
                    onMouseLeave={onPointerUp}
                    onTouchStart={onPointerDown}
                    onTouchMove={onPointerMove}
                    onTouchEnd={onPointerUp}
                />
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-100 border-t border-gray-300 safe-area-bottom">
                {TOOLS.map(t => (
                    <button
                        key={t.id}
                        type="button"
                        onClick={() => setTool(t.id)}
                        className={`px-3 py-2 text-sm font-medium rounded transition-colors
                            ${tool === t.id
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                    >
                        {t.label}
                    </button>
                ))}

                <div className="w-px h-8 bg-gray-300 mx-1" />

                <button
                    type="button"
                    onClick={handleUndo}
                    className="px-3 py-2 text-sm font-medium rounded bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                >
                    Undo
                </button>
                <button
                    type="button"
                    onClick={handleClear}
                    className="px-3 py-2 text-sm font-medium rounded bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                >
                    Clear
                </button>

                <div className="w-px h-8 bg-gray-300 mx-1" />

                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                    type="button"
                    onClick={handleBack}
                    className="px-3 py-2 text-sm font-medium rounded bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
                >
                    Back
                </button>
            </div>
        </div>
    )
}